import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { authApi } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { NotificationPreferences } from "@/components/notifications/NotificationPreferences";
import { TwoFactorSettings } from "@/components/settings/TwoFactorSettings";
import { DeviceSessions } from "@/components/settings/DeviceSessions";
import { User, Lock, Bell, Trash2, Eye, EyeOff, X } from "lucide-react";
import { PageMeta, DASHBOARD_PAGE_META } from "@/lib/seo";
import { cn } from "@/lib/utils";

// ═══════════════════════════════════════════════════════════════════
// Password requirements helper (matches RegisterForm / AuthModal)
// ═══════════════════════════════════════════════════════════════════

function getPasswordRequirements(password: string) {
  return {
    minLength: password.length >= 10,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };
}

function isPasswordValid(password: string): boolean {
  return Object.values(getPasswordRequirements(password)).every(Boolean);
}

const PASSWORD_CRITERIA = [
  { key: "minLength" as const, label: "Minst 10 tecken" },
  { key: "hasUppercase" as const, label: "Minst en versal (A-Z)" },
  { key: "hasLowercase" as const, label: "Minst en gemen (a-z)" },
  { key: "hasNumber" as const, label: "Minst en siffra (0-9)" },
  { key: "hasSpecial" as const, label: "Minst ett specialtecken (!@#$%...)" },
];

// ═══════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════

export function Settings() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"profile" | "security" | "notifications">(
    "profile"
  );

  const tabs = [
    {
      id: "profile" as const,
      name: "Profil",
      description: "Namn och kontaktuppgifter",
      icon: User
    },
    {
      id: "security" as const,
      name: "Säkerhet",
      description: "Lösenord, 2FA och sessioner",
      icon: Lock
    },
    {
      id: "notifications" as const,
      name: "Notifikationer",
      description: "E-post och aviseringar",
      icon: Bell
    },
  ];

  return (
    <>
      <PageMeta
        title={DASHBOARD_PAGE_META.settings.title}
        description={DASHBOARD_PAGE_META.settings.description}
        noIndex
      />
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Inställningar</h1>
          <p className="text-sm sm:text-base text-gray-500 mt-1">Hantera ditt konto och preferenser</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-72 flex-shrink-0">
            {/* Mobile: Horizontal scrollable tabs */}
            <div className="lg:hidden flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all",
                    activeTab === tab.id
                      ? "bg-gray-900 text-white shadow-md"
                      : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  )}
                >
                  <tab.icon size={16} />
                  <span>{tab.name}</span>
                </button>
              ))}
            </div>

            {/* Desktop: Vertical navigation with card */}
            <nav className="hidden lg:block bg-white rounded-xl border border-gray-200 overflow-hidden">
              {tabs.map((tab, index) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "w-full flex items-start gap-4 px-5 py-4 text-left transition-all relative",
                    index !== tabs.length - 1 && "border-b border-gray-100",
                    activeTab === tab.id
                      ? "bg-gradient-to-r from-primary-50 to-transparent"
                      : "hover:bg-gray-50"
                  )}
                >
                  {/* Active indicator */}
                  {activeTab === tab.id && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
                  )}

                  {/* Icon */}
                  <div className={cn(
                    "flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors",
                    activeTab === tab.id
                      ? "bg-primary-100 text-primary-700"
                      : "bg-gray-100 text-gray-500"
                  )}>
                    <tab.icon size={20} />
                  </div>

                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <span className={cn(
                      "block font-semibold transition-colors",
                      activeTab === tab.id ? "text-gray-900" : "text-gray-700"
                    )}>
                      {tab.name}
                    </span>
                    <span className="block text-sm text-gray-500 mt-0.5">
                      {tab.description}
                    </span>
                  </div>
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {activeTab === "profile" && (
              <ProfileForm user={user} onSaved={refreshUser} />
            )}
            {activeTab === "security" && (
              <SecuritySection onLogout={logout} navigate={navigate} />
            )}
            {activeTab === "notifications" && <NotificationsSection />}
          </div>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Profile Form
// ═══════════════════════════════════════════════════════════════════

function ProfileForm({
  user,
  onSaved,
}: {
  user: { firstName: string | null; lastName: string | null; email: string } | null;
  onSaved: () => Promise<void>;
}) {
  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [isSaving, setIsSaving] = useState(false);

  // Sync state when user data changes (e.g. after refresh)
  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const response = await authApi.updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      if (response.success) {
        toast.success("Profilen har uppdaterats");
        await onSaved();
      } else {
        toast.error(response.error?.message || "Kunde inte uppdatera profilen");
      }
    } catch {
      toast.error("Ett fel uppstod");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="card">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        Profilinformation
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">
              Förnamn
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="input"
              placeholder="Ditt förnamn"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">
              Efternamn
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="input"
              placeholder="Ditt efternamn"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            E-postadress
          </label>
          <input
            type="email"
            value={user?.email || ""}
            className="input"
            disabled
          />
          <p className="text-sm text-gray-400 mt-1">
            Kontakta support för att ändra e-postadress
          </p>
        </div>

        <div className="pt-4">
          <button type="submit" className="btn btn-primary" disabled={isSaving}>
            {isSaving ? <Spinner size="sm" /> : null}
            Spara ändringar
          </button>
        </div>
      </form>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Security Section (password change + delete account)
// ═══════════════════════════════════════════════════════════════════

function SecuritySection({
  onLogout,
  navigate,
}: {
  onLogout: () => Promise<void>;
  navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <div className="space-y-6">
      <TwoFactorSettings />
      <DeviceSessions />
      <ChangePasswordForm onLogout={onLogout} navigate={navigate} />
      <DeleteAccountCard onLogout={onLogout} navigate={navigate} />
    </div>
  );
}

function ChangePasswordForm({
  onLogout,
  navigate,
}: {
  onLogout: () => Promise<void>;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const requirements = getPasswordRequirements(newPassword);
  const passwordValid = isPasswordValid(newPassword);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit =
    currentPassword.length > 0 && passwordValid && passwordsMatch && !isSaving;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSaving(true);
    try {
      const response = await authApi.changePassword(currentPassword, newPassword);
      if (response.success) {
        toast.success("Lösenordet har uppdaterats. Du loggas ut.");
        // Sessions are invalidated server-side, so log out the client
        await onLogout();
        navigate("/");
      } else {
        toast.error(response.error?.message || "Kunde inte byta lösenord");
      }
    } catch {
      toast.error("Ett fel uppstod");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="card">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Byt lösenord</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Current password */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Nuvarande lösenord
          </label>
          <div className="relative">
            <input
              type={showCurrent ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="input pr-10"
              placeholder="Ange ditt nuvarande lösenord"
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-900"
              tabIndex={-1}
            >
              {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {/* New password */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Nytt lösenord
          </label>
          <div className="relative">
            <input
              type={showNew ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input pr-10"
              placeholder="Välj ett nytt lösenord"
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-900"
              tabIndex={-1}
            >
              {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* Password requirements */}
          {newPassword.length > 0 && (
            <ul className="mt-2 space-y-1">
              {PASSWORD_CRITERIA.map((c) => (
                <li
                  key={c.key}
                  className={cn(
                    "flex items-center space-x-2 text-sm",
                    requirements[c.key] ? "text-green-600" : "text-gray-400"
                  )}
                >
                  <span>{requirements[c.key] ? "\u2713" : "\u25CB"}</span>
                  <span>{c.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Confirm new password */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Bekräfta nytt lösenord
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="input"
            placeholder="Ange det nya lösenordet igen"
          />
          {confirmPassword.length > 0 && !passwordsMatch && (
            <p className="text-sm text-red-600 mt-1">Lösenorden matchar inte</p>
          )}
        </div>

        <div className="pt-4">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!canSubmit}
          >
            {isSaving ? <Spinner size="sm" /> : null}
            Uppdatera lösenord
          </button>
        </div>
      </form>
    </div>
  );
}

function DeleteAccountCard({
  onLogout,
  navigate,
}: {
  onLogout: () => Promise<void>;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <div className="card border-red-200">
        <h2 className="text-xl font-semibold text-red-600 mb-2">
          Radera konto
        </h2>
        <p className="text-gray-500 mb-4">
          När du raderar ditt konto försvinner all data permanent. Denna åtgärd
          kan inte ångras.
        </p>
        <button className="btn btn-danger" onClick={() => setShowModal(true)}>
          <Trash2 size={18} />
          Radera mitt konto
        </button>
      </div>

      {showModal && (
        <DeleteAccountModal
          onClose={() => setShowModal(false)}
          onLogout={onLogout}
          navigate={navigate}
        />
      )}
    </>
  );
}

function DeleteAccountModal({
  onClose,
  onLogout,
  navigate,
}: {
  onClose: () => void;
  onLogout: () => Promise<void>;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [password, setPassword] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  const handleDelete = useCallback(async () => {
    if (!password) return;
    setIsDeleting(true);
    try {
      const response = await authApi.deleteAccount(password);
      if (response.success) {
        toast.success("Ditt konto har raderats");
        await onLogout();
        navigate("/");
      } else {
        toast.error(response.error?.message || "Kunde inte radera kontot");
      }
    } catch {
      toast.error("Ett fel uppstod");
    } finally {
      setIsDeleting(false);
    }
  }, [password, onLogout, navigate]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isDeleting) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, isDeleting]);

  // Track mousedown to prevent closing when dragging from inside modal
  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (
      !isDeleting &&
      e.target === overlayRef.current &&
      mouseDownTargetRef.current === overlayRef.current
    ) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-red-600">
            Bekräfta radering
          </h2>
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-500">
            Denna åtgärd är permanent och kan inte ångras. All din data, inklusive
            företag och årsredovisningar, kommer att raderas.
          </p>
          <p className="text-sm text-gray-500">
            Ange ditt lösenord för att bekräfta:
          </p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
            placeholder="Ditt lösenord"
            autoFocus
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200">
          <button
            className="btn btn-outline"
            onClick={onClose}
            disabled={isDeleting}
          >
            Avbryt
          </button>
          <button
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={!password || isDeleting}
          >
            {isDeleting ? <Spinner size="sm" /> : <Trash2 size={16} />}
            Radera permanent
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Notifications Section
// ═══════════════════════════════════════════════════════════════════

function NotificationsSection() {
  return (
    <div className="card">
      <NotificationPreferences />
    </div>
  );
}

